import { apiFetch, keepaliveFetch } from '@/lib/api-fetch.js';

/**
 * The page a user had open last, kept in their preferences so any device can open where they left
 * off. `href` is an app-relative path with its query; it is checked again before it is ever used.
 */
export interface LastLocation {
  href: string;
  workspaceId: string | null;
  at: string;
}

const ENDPOINT = '/v1/me/preferences';

const JSON_HEADERS: Record<string, string> = {
  'Content-Type': 'application/json',
};

/** The user's preferences object, exactly as the service returns it: unwrapped, one key per setting. */
export async function fetchPreferences(): Promise<Record<string, unknown>> {
  const response = await apiFetch(ENDPOINT, { headers: JSON_HEADERS });
  if (!response.ok) {
    throw new Error(`Failed to load preferences (${response.status})`);
  }
  const body: unknown = await response.json();
  return body !== null && typeof body === 'object' && !Array.isArray(body)
    ? (body as Record<string, unknown>)
    : {};
}

/**
 * Stores the page the user is on. Only the `last_location` key is sent, and the service merges keys
 * one level deep, so every other preference is left as it is.
 */
export async function saveLastLocation(
  location: LastLocation,
  options: { keepalive?: boolean } = {},
): Promise<void> {
  const init: RequestInit = {
    method: 'PUT',
    headers: JSON_HEADERS,
    body: JSON.stringify({
      data: {
        last_location: {
          href: location.href,
          workspace_id: location.workspaceId,
          at: location.at,
        },
      },
    }),
  };
  const response = options.keepalive
    ? await keepaliveFetch(ENDPOINT, init)
    : await apiFetch(ENDPOINT, init);
  if (!response.ok) {
    throw new Error(`Failed to save the last page (${response.status})`);
  }
}

const FAVORITE_SUITES_KEY = 'quality_favorite_suites';

/** The suites the user starred on the Quality Tests tab. A missing or malformed value reads as none. */
export function favoriteSuiteKeysOf(
  preferences: Record<string, unknown>,
): string[] {
  const stored = preferences[FAVORITE_SUITES_KEY];
  return Array.isArray(stored)
    ? stored.filter((key): key is string => typeof key === 'string')
    : [];
}

/**
 * Stores the starred suites. Only the `quality_favorite_suites` key is sent, and the service merges
 * keys one level deep, so the list is replaced whole and every other preference is left as it is.
 */
export async function saveFavoriteSuiteKeys(keys: string[]): Promise<void> {
  const response = await apiFetch(ENDPOINT, {
    method: 'PUT',
    headers: JSON_HEADERS,
    body: JSON.stringify({ data: { [FAVORITE_SUITES_KEY]: keys } }),
  });
  if (!response.ok) {
    throw new Error(`Failed to save favorite suites (${response.status})`);
  }
}
